import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Select, MenuItem, FormControl, InputLabel,
  Paper, Chip, Alert, IconButton,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add, Settings, Refresh, Delete, Edit, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { RendimientoService } from '../services/rendimientoService';
import { PeriodoService } from '../../periodos/services/periodoService';
import type { AsistenciaRendimiento, RendimientoResumen } from '../types/rendimiento';
import type { PeriodoPlanilla } from '../../periodos/types/periodo.types';
import { ESTADOS_RENDIMIENTO } from '../types/rendimiento';
import MasterDataDialog from './MasterDataDialog';

const RendimientoPage: React.FC = () => {
  const navigate = useNavigate();
  const [periodos, setPeriodos] = useState<PeriodoPlanilla[]>([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState<number>(0);
  const [documentos, setDocumentos] = useState<AsistenciaRendimiento[]>([]);
  const [resumen, setResumen] = useState<RendimientoResumen | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [masterOpen, setMasterOpen] = useState(false);

  useEffect(() => {
    loadPeriodos();
  }, []);

  useEffect(() => {
    if (selectedPeriodo) loadData();
  }, [selectedPeriodo]);

  const loadPeriodos = async () => {
    try {
      const data = await PeriodoService.getAll();
      const sorted = data.sort((a, b) => b.anio - a.anio || b.mes - a.mes);
      setPeriodos(sorted);
      if (sorted.length > 0) setSelectedPeriodo(sorted[0].idPeriodo);
    } catch { /* interceptor */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [docs, res] = await Promise.all([
        RendimientoService.getDocumentos(selectedPeriodo),
        RendimientoService.getResumen(selectedPeriodo),
      ]);
      setDocumentos(docs);
      setResumen(res);
    } catch { /* interceptor */ }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminar este documento?')) return;
    try {
      await RendimientoService.deleteDocumento(id);
      setMsg('Documento eliminado');
      loadData();
    } catch { /* interceptor */ }
  };

  const getEstadoChip = (estado: string) => {
    const e = ESTADOS_RENDIMIENTO.find(x => x.value === estado);
    return <Chip label={e?.label || estado} size="small" sx={{ bgcolor: e?.color || '#999', color: '#fff' }} />;
  };

  const columns: GridColDef[] = [
    {
      field: 'fecha', headerName: 'Fecha', width: 110,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'turno', headerName: 'Turno', width: 130,
      valueGetter: (_value: any, row: AsistenciaRendimiento) => row.turno?.descripcion || '-',
    },
    {
      field: 'grupoTrabajo', headerName: 'Grupo Trabajo', width: 150,
      valueGetter: (_value: any, row: AsistenciaRendimiento) => row.grupoTrabajo?.descripcion || '-',
    },
    {
      field: 'responsable', headerName: 'Responsable', width: 180,
      valueGetter: (_value: any, row: AsistenciaRendimiento) => row.responsable?.personal?.personaContacto || '-',
    },
    {
      field: 'estado', headerName: 'Estado', width: 110,
      renderCell: (params) => getEstadoChip(params.value),
    },
    { field: 'cantidadTrabajadores', headerName: 'Trabajadores', width: 100, type: 'number' },
    {
      field: 'totalRendimiento', headerName: 'Total Rend. S/', width: 120, type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      field: 'promedioRendimiento', headerName: 'Prom. S/', width: 100, type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      field: 'acciones', headerName: 'Acciones', width: 120, sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => navigate(`/planilla/rendimiento/${params.row.idAsistenciaRendimiento}`)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.idAsistenciaRendimiento)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const periodoLabel = (p: PeriodoPlanilla) =>
    `${p.anio}-${String(p.mes).padStart(2, '0')} (${p.tipoPeriodo})`;

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Asistencia por Rendimiento</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Settings />} onClick={() => setMasterOpen(true)}>
            Datos Maestros
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/planilla/rendimiento/nuevo')}>
            Nuevo Documento
          </Button>
        </Box>
      </Box>

      {msg && <Alert severity="success" onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg}</Alert>}

      {/* Period Selector */}
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Periodo</InputLabel>
          <Select label="Periodo" value={selectedPeriodo}
            onChange={e => setSelectedPeriodo(e.target.value as number)}>
            {periodos.map(p => (
              <MenuItem key={p.idPeriodo} value={p.idPeriodo}>{periodoLabel(p)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={loadData} disabled={loading}><Refresh /></IconButton>
      </Box>

      {/* Summary */}
      {resumen && (
        <Box display="flex" gap={2} mb={2}>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4">{resumen.totalDocumentos}</Typography>
            <Typography variant="body2" color="text.secondary">Documentos</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4">{resumen.totalTrabajadores}</Typography>
            <Typography variant="body2" color="text.secondary">Trabajadores</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4">S/ {resumen.totalRendimiento.toFixed(2)}</Typography>
            <Typography variant="body2" color="text.secondary">Total Rendimiento</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4">S/ {resumen.promedioRendimiento.toFixed(2)}</Typography>
            <Typography variant="body2" color="text.secondary">Promedio Rendimiento</Typography>
          </Paper>
        </Box>
      )}

      {/* Documents Grid */}
      <Paper sx={{ height: 500 }}>
        <DataGrid
          rows={documentos}
          columns={columns}
          getRowId={r => r.idAsistenciaRendimiento}
          loading={loading}
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>

      <MasterDataDialog open={masterOpen} onClose={() => setMasterOpen(false)} />
    </Box>
  );
};

export default RendimientoPage;
